import { useMemo } from 'react';
import { tokenStore } from '@/auth/tokenStore';
import { decodeAccessToken } from '@/auth/tokenClaims';
import { useOptionalAuth } from '@/stores/authContext';

/**
 * Текущий пользователь — агент (ДАТА1861)? Агент посредничает по сделкам, но НЕ публикует
 * собственные заявки на покупку (прайсы) и предложения о продаже. Роль берём из access-токена
 * (плоский claim roles). Не реактивно к refresh — но роли в пределах сессии не меняются.
 *
 * Сессию читаем мягко (useOptionalAuth): хук вызывается и из низкоуровневых презентационных
 * компонентов (BestPriceLink), которые в изолированных тестах рендерятся вне AuthProvider —
 * там это просто «не агент», без исключения.
 */
export function useIsAgent(): boolean {
  const isAuthenticated = useOptionalAuth()?.isAuthenticated ?? false;
  return useMemo(() => {
    if (!isAuthenticated) {
      return false;
    }
    const claims = decodeAccessToken(tokenStore.getAccess());
    return claims?.roles.includes('AGENT') ?? false;
  }, [isAuthenticated]);
}

/**
 * Текущий пользователь — владелец-менеджер своей организации? Только он приглашает и удаляет
 * сотрудников; остальные сотрудники работают от лица компании, но компанией не управляют.
 * Роль назначается при регистрации владельца и приходит в access-токене (плоский claim roles).
 *
 * Скрытие раздела в интерфейсе — удобство, а не защита: доступ разграничивает бэкенд.
 */
export function useIsManager(): boolean {
  const isAuthenticated = useOptionalAuth()?.isAuthenticated ?? false;
  return useMemo(() => {
    if (!isAuthenticated) {
      return false;
    }
    const claims = decodeAccessToken(tokenStore.getAccess());
    return claims?.roles.includes('MANAGER') ?? false;
  }, [isAuthenticated]);
}

/**
 * Текущий пользователь — перевозчик? Роль CARRIER (группа Identix `carriers`) приходит в access-токене
 * (плоский claim roles). Не реактивно к refresh — но роли в пределах сессии не меняются. Скрытие/показ
 * разделов перевозчика в интерфейсе — удобство навигации, а не ограничение доступа: доступ к данным
 * разграничивает бэкенд (роль CARRIER + аттестация организации).
 */
export function useIsCarrier(): boolean {
  const isAuthenticated = useOptionalAuth()?.isAuthenticated ?? false;
  return useMemo(() => {
    if (!isAuthenticated) {
      return false;
    }
    const claims = decodeAccessToken(tokenStore.getAccess());
    return claims?.roles.includes('CARRIER') ?? false;
  }, [isAuthenticated]);
}
